import axios from 'axios';
import { getUrl } from './service-utils';
import authHeader from './auth-header';

/**
 * Location bias for place searches (matches backend API)
 */
export interface IActivityBias {
  lat: number;
  lng: number;
  radius: number;
}

/**
 * Place response from backend API (legacy format from places controller)
 * This matches what the backend controller actually returns in getPlaceDetails
 */
export interface PlaceResponseDto {
  id: string;
  title: string;
  name?: string; // Some responses include name
  description?: string;
  location: {
    latitude: number;
    longitude: number;
  };
  // Direct coordinate access for convenience
  latitude?: number;
  longitude?: number;
  websiteUrl?: string;
  types?: string[];
  primaryType?: string;
  imageUrl?: string;
  images?: string[];
  imagesLink?: string;
  addressComponents?: any[];
  displayName?: {
    text: string;
    languageCode?: string;
  };
  // DDD fields (when returned from searchPlaces)
  googlePlacesId?: string;
  gpsCoordinates?: {
    latitude: number;
    longitude: number;
  };
  rating?: number;
  placeType?: string;
  allTypes?: string[];
  contactInfo?: {
    phone?: string;
    website?: string;
    email?: string;
  };
  status?: string;
  createdAt?: Date;
  updatedAt?: Date;
  address?: string;
  priceLevel?: string;
  userRatingsTotal?: number;
  photos?: Array<{
    name: string;
    widthPx?: number;
    heightPx?: number;
    authorAttributions?: Array<{
      displayName?: string;
      uri?: string;
      photoUri?: string;
    }>;
  }>;
  reviews?: any[];
  businessStatus?: string;
  vicinity?: string;
  parentPlaceId?: string;
  ancestorTreeIds?: string[];
  thumbnail?: string;
  serpapiThumbnail?: string;
}

/**
 * Autocomplete prediction from backend API
 * Supporting both legacy and new formats
 */
export interface AutocompletePrediction {
  place_id?: string;
  placeId?: string;
  description?: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
  types?: string[];
  // Legacy format support
  placePrediction?: {
    placeId: string;
    text?: {
      text: string;
    };
    structuredFormat?: {
      mainText?: {
        text: string;
      };
      secondaryText?: {
        text: string;
      };
    };
    types?: string[];
  };
}

/**
 * Service for interacting with Places API
 * All Google Places searches go through the backend for cost control and security
 */
class PlacesService {
  private API_URL: string;

  constructor() {
    this.API_URL = getUrl();
  }

  /**
   * Search for places by text query
   * Uses backend API which searches both local DB and Google Places
   *
   * @param query - Search query (e.g., "pizza", "museums")
   * @param bias - Optional location bias for search results
   * @returns Array of place results
   */
  async searchPlaces(
    query: string,
    bias?: IActivityBias | null
  ): Promise<PlaceResponseDto[]> {
    try {
      console.log('[PlacesService] Searching places:', { query, bias });

      const response = await axios.post(
        `${this.API_URL}places/search`,
        { query, bias },
        { headers: authHeader() }
      );

      const places = response.data.data?.places || [];
      console.log(`[PlacesService] Found ${places.length} places`);

      return places;
    } catch (error: any) {
      console.error(
        '[PlacesService] Error searching places:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Search for places using Google Text Search API
   * More powerful than nearby search, supports natural language queries
   *
   * @param textQuery - Natural language search query (e.g., "best pizza in downtown")
   * @param locationBias - Optional location bias with coordinates and radius
   * @param viewport - Optional map viewport bounds to filter results
   * @param includedTypes - Optional array of place types to filter results
   * @param maxResultCount - Maximum number of results (max 20)
   * @param rankPreference - Sort by 'DISTANCE' or 'RELEVANCE' (default)
   * @returns Object with places array as TripActivityDto objects
   */
  async searchText(params: {
    textQuery: string;
    locationBias?: {
      title?: string; // Location name for context
      latitude: number;
      longitude: number;
      radius?: number; // in meters
    };
    viewport?: {
      north: number;
      south: number;
      east: number;
      west: number;
    };
    includedTypes?: string[];
    maxResultCount?: number;
    rankPreference?: 'DISTANCE' | 'RELEVANCE';
  }): Promise<{ places: PlaceResponseDto[] }> {
    try {
      console.log('[PlacesService] Text search:', params);

      const response = await axios.post(
        `${this.API_URL}maps/textsearch`,
        params,
        { headers: authHeader() }
      );

      const results = response.data.data;
      console.log(
        `[PlacesService] Text search found ${results?.places?.length || 0} places (TripActivityDto format from backend)`
      );

      return results;
    } catch (error: any) {
      console.error(
        '[PlacesService] Error in text search:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get autocomplete suggestions for place search
   *
   * @param input - User input text
   * @param sessionToken - Optional session token for billing
   * @param bias - Optional location bias
   * @returns Object with predictions array and status
   */
  async getAutocompleteSuggestions(
    input: string,
    sessionToken?: string,
    bias?: IActivityBias | null
  ): Promise<{ predictions: AutocompletePrediction[]; status: string }> {
    try {
      console.log('[PlacesService] Autocomplete request:', {
        input,
        sessionToken,
        bias
      });

      const response = await axios.post(
        `${this.API_URL}places/autocomplete`,
        { input, sessionToken, bias },
        { headers: authHeader() }
      );

      console.log('[PlacesService] Autocomplete raw response:', response.data);
      console.log(
        '[PlacesService] Autocomplete response.data.data:',
        response.data.data
      );

      return {
        predictions: response.data.data.suggestions || [],
        status: response.data.status || 'UNKNOWN'
      };
    } catch (error: any) {
      console.error(
        '[PlacesService] Error getting autocomplete:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get detailed place information by Place ID
   *
   * @param placeId - Google Place ID
   * @param sessionToken - Optional session token
   * @returns Detailed place information
   */
  async getPlaceDetails(
    placeId: string,
    sessionToken?: string
  ): Promise<PlaceResponseDto> {
    try {
      const response = await axios.get(`${this.API_URL}places/${placeId}`, {
        params: { sessionToken },
        headers: authHeader()
      });

      // Backend returns: { status: 'success', data: returnedPlace }
      return response.data.data;
    } catch (error: any) {
      console.error(
        '[PlacesService] Error getting place details:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get place information from coordinates (reverse geocoding)
   *
   * @param latitude - Latitude coordinate
   * @param longitude - Longitude coordinate
   * @returns Place information
   */
  async getPlaceFromCoordinates(
    latitude: number,
    longitude: number
  ): Promise<PlaceResponseDto> {
    try {
      const response = await axios.post(
        `${this.API_URL}places/coordinates`,
        { latitude, longitude },
        { headers: authHeader() }
      );

      return response.data.data.place;
    } catch (error: any) {
      console.error(
        '[PlacesService] Error getting place from coordinates:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get multiple place details in batch
   *
   * @param placeIds - Array of Google Place IDs
   * @param sessionToken - Optional session token
   * @returns Array of place details
   */
  async getBatchPlaceDetails(
    placeIds: string[],
    sessionToken?: string
  ): Promise<PlaceResponseDto[]> {
    try {
      const response = await axios.post(
        `${this.API_URL}places/batch`,
        { placeIds, sessionToken },
        { headers: authHeader() }
      );

      return response.data.data.places;
    } catch (error: any) {
      console.error(
        '[PlacesService] Error getting batch place details:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }
}

const placesService = new PlacesService();
export default placesService;
